import { HeadingLarge, theme } from '@arnold/common';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardQuery, Features, OrganizationQuery, SurveyStatus } from '../../generated/hooks';
import { handleSummaryResultsClick } from '../../lib/common';
import { isSurveyActive } from '../../lib/helpers';
import { useFeatureFlag } from '../../lib/helpers/hooks';
import { ActiveSurveys } from './ActiveSurveys';
import { CompletedSurveys } from './CompletedSurveys';
import { Onboarding } from './Onboarding';
import { ProcessSurveys } from './ProcessSurveys';
import { Pulser } from './Pulser';

type DashboardProps = {
  organization: NonNullable<OrganizationQuery['organization']>;
  dashboardData: DashboardQuery['user']['organization'];
  user: NonNullable<DashboardQuery['user']>;
};

export const Dashboard: FC<DashboardProps> = ({ organization, dashboardData, user }) => {
  const { t } = useTranslation('dashboard');
  const hasPulserModule = useFeatureFlag(Features.PulserModule);
  const currentSurveys = dashboardData?.currentSurveys ?? [];
  const activeSurveys = currentSurveys.filter((survey) => isSurveyActive(survey.status));
  const completedSurveys = currentSurveys.filter((survey) => survey.status === SurveyStatus.Completed);

  const onboardings = dashboardData?.activeOnboadings ?? [];
  // With Pulser enabled, onboarding stays below the survey sections. Otherwise an
  // active onboarding is shown above them.
  const showOnboardingAboveSurveys = !hasPulserModule && onboardings.length > 0;

  const isWithoutSurveys =
    currentSurveys.length === 0 ||
    (dashboardData?.activeProcessSurveys.length === 0 && dashboardData.activeOnboadings.length === 0);
  return (
    <>
      <HeadingLarge margin={`128px 0 ${theme.spacing.f}`} data-icom={'heading-dashboard'} data-cy={'heading-dashboard'}>
        {t('overview')}
      </HeadingLarge>
      {hasPulserModule && <Pulser enabledRespondentsCount={organization.enabledRespondentsCount} />}
      {showOnboardingAboveSurveys && <Onboarding surveyGroups={onboardings} organization={organization} />}
      <ActiveSurveys
        surveys={activeSurveys}
        enabledRespondentsCount={organization.enabledRespondentsCount}
        organization={organization}
        isWithoutSurveys={isWithoutSurveys}
        onResultsClick={() => handleSummaryResultsClick(user.id, organization.id)}
      />
      <ProcessSurveys
        organization={organization}
        activeProcessSurveys={dashboardData?.activeProcessSurveys}
        onResultsClick={() => handleSummaryResultsClick(user.id, organization.id)}
      />
      <CompletedSurveys
        surveys={completedSurveys}
        onResultsClick={() => handleSummaryResultsClick(user.id, organization.id)}
      />
      {!showOnboardingAboveSurveys && <Onboarding surveyGroups={onboardings} organization={organization} />}
    </>
  );
};
